<script setup lang="ts">
    import Invoice from '../../../app/invoicing/models/Invoice';
    import EndpointFactory from '../../../app/factories/EndpointFactory';
    import InvoiceEndpoint from '../../../app/invoicing/endpoints/InvoiceEndpoint';
    import { useEvent } from '@/composables/useEventBus';
    import { ModelType } from '~~/app/base/schemas/BaseSchema';
    import { InvoiceDocumentType } from '~/app/invoicing/enums/InvoiceDocumentType';

    interface Fields {
        invoice: Invoice | null;
    }

    const form = useForm<Fields>({
        invoice: null,
    });

    const endpoint = EndpointFactory.make(ModelType.INVOICES) as InvoiceEndpoint;

    const submit = async () => {
        const response = await endpoint.replicate(form.fields.invoice?.getId(), {
            documentType: InvoiceDocumentType.CREDIT_NOTE,
            shouldKeepContact: true,
        });

        if (response.data) {
            const text = useTranslate('invoicing.credit_notes.modals.success') + form.fields.invoice?.number + '!';
            useEvent(`invoicing:invoice:invoice-modal:close`);
            useToasteoSuccess(text);
            response.data.edit();
        }
    };

    const close = () => {
        form.reset();
        useEvent(`invoicing:invoice:invoice-modal:close`);
    };

    useModal('invoicing:invoice:invoice-modal', () => {});
</script>

<template>
    <div>
        <Modal
            ref="modal"
            name="invoicing:invoice:invoice-modal"
        >
            <div :class="$theme('modal.title.container')">
                <h2 :class="$theme('modal.title.text')">
                    {{ $t('invoicing.credit-note_create_form.title') }}
                </h2>
            </div>
            <form @submit.prevent="submit">
                <div
                    :class="$theme('modal.padding')"
                    class="space-y-4"
                >
                    <FormQuoteSelect
                        :document-type="InvoiceDocumentType.INVOICE"
                        :form="form"
                        input-name="invoice"
                        :label="$t('form.labels.invoice')"
                    />
                </div>
                <div :class="$theme('modal.footer.container')">
                    <div :class="$theme('modal.footer.button_container')">
                        <LoadingButton
                            :extra-class-name="$theme('modal.footer.button')"
                            :class-name="$theme('button.style.cancel')"
                            prevent-default
                            @clicked="close()"
                            >{{ $t('actions.cancel') }}</LoadingButton
                        >
                        <LoadingButton
                            :extra-class-name="$theme('modal.footer.button')"
                            :loading="form.isLoading"
                            type="submit"
                            >{{ $t('actions.create') }}</LoadingButton
                        >
                    </div>
                </div>
            </form>
        </Modal>
    </div>
</template>
